import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import api, { extractErr } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { parseDirectCheckoutSearch } from "@/lib/directCheckout";
import { useCustomerAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card } from "@/components/ui/card";
import { load } from "@cashfreepayments/cashfree-js";
import { toast } from "sonner";
import { useStoreBase } from "@/lib/store";
import { getOndemandPin, getOndemandPinLabel } from "@/lib/ondemandPin";
import { isOndemandStorefront, storefrontThemeId } from "@/lib/storefrontTheme";
import { useTranslation } from "react-i18next";
import { cartItemsPayload } from "@/lib/customForm";
import { money, symbolOf } from "@/lib/money";
import { IN_STATES } from "@/lib/indiaShipping";
import { gokwikReady, openGokwikCheckout } from "@/lib/gokwik";
import { fastrrReady, openFastrrCheckout } from "@/lib/fastrr";
import { deliverySlots, slotPrompt, slotsRequired, storeOrdersPaused, storeOfflineMessage } from "@/lib/timeslotCheckout";
import { trackStoreEvent } from "@/lib/storeAds";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function StorefrontCheckout({ config }) {
  const { t } = useTranslation();
  const { slug, base, country } = useStoreBase();
  const nav = useNavigate();
  const loc = useLocation();
  const boxOrder = loc.state?.buildBox || null;
  const abpOrder = loc.state?.abpMix || null;
  const [params, setParams] = useSearchParams();
  const { items, subtotal, clear, replaceWith, loaded } = useCart();
  const [buying, setBuying] = useState(Boolean(params.get("buy")));
  const [buyErr, setBuyErr] = useState("");
  const { user, refresh } = useCustomerAuth();
  const guestAllowed = config.preferences?.guest_checkout !== false ? Boolean(config.preferences?.guest_checkout) : false;
  const checkoutTracked = useRef(false);
  useEffect(() => {
    if (!loaded || checkoutTracked.current) return;
    checkoutTracked.current = true;
    trackStoreEvent("begin_checkout", {
      value: items.reduce((sum, i) => sum + Number(i.price || 0) * Number(i.qty || 1), 0),
      currency: "INR",
      content_ids: items.map((i) => i.id).filter(Boolean),
    });
  }, [loaded, items]);

  const zeploDelivery = isOndemandStorefront(config) || (storefrontThemeId(config) === "classic" && Boolean(config?.zeplo_classic_active));
  const ondemandPin = zeploDelivery ? (getOndemandPin(slug) || "") : "";
  const ondemandCity = zeploDelivery ? (getOndemandPinLabel(slug) || "") : "";
  const [address, setAddress] = useState({ name: "", phone: "", line1: "", line2: "", city: ondemandCity, state: "", pincode: ondemandPin, country: "India" });
  const [contact, setContact] = useState({ email: "", name: "" });
  const [password, setPassword] = useState("");
  const [emailExists, setEmailExists] = useState(false);
  const [payment, setPayment] = useState(() => {
    const p = config.payment || {};
    const options = [
      ["cod", p.cod_enabled],
      ["razorpay", p.razorpay_enabled],
      ["cashfree", p.cashfree_enabled],
      ["instamojo", p.instamojo_enabled],
      ["payu", p.payu_enabled],
      ["phonepe", p.phonepe_enabled],
      ["stripe", p.stripe_enabled],
      ["paypal", p.paypal_enabled],
      ["whatsapp", p.whatsapp_enabled],
    ];
    return (options.find(([, on]) => on) || ["cod"])[0];
  });
  const [notes, setNotes] = useState("");
  const [deliverySlotId, setDeliverySlotId] = useState("");
  const paused = storeOrdersPaused(config);
  const needSlot = slotsRequired(config);
  const slotList = deliverySlots(config);
  const [quote, setQuote] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponErr, setCouponErr] = useState("");
  const [useWallet, setUseWallet] = useState(false);
  const [walletInfo, setWalletInfo] = useState(null);
  const [onlinePopup, setOnlinePopup] = useState(null);
  const gkLaunched = useRef(false);
  const frLaunched = useRef(false);
  const nativeCheckout = params.get("native") === "1" || Boolean(boxOrder) || Boolean(abpOrder);
  const useGokwik = gokwikReady(config, country) && !nativeCheckout;
  const useFastrr = fastrrReady(config, country) && !nativeCheckout && !useGokwik;

  const buyKey = params.get("buy") || "";
  const buyVariant = params.get("v") || "";
  const buyQty = params.get("qty") || "";

  useEffect(() => {
    if (user) setContact({ email: user.email || "", name: user.name || "" });
  }, [user]);

  useEffect(() => {
    if (user || guestAllowed) {
      setEmailExists(false);
      return undefined;
    }
    const email = contact.email.trim();
    if (!email.includes("@")) {
      setEmailExists(false);
      return undefined;
    }
    let alive = true;
    const timer = setTimeout(() => {
      api.post(`/store/${slug}/checkout/account-check`, { email })
        .then(({ data }) => { if (alive) setEmailExists(Boolean(data?.exists)); })
        .catch(() => { if (alive) setEmailExists(false); });
    }, 400);
    return () => { alive = false; clearTimeout(timer); };
  }, [contact.email, user, guestAllowed, slug]);

  useEffect(() => {
    if (!loaded) return;
    if (!buyKey) {
      setBuying(false);
      return;
    }
    const spec = parseDirectCheckoutSearch(params.toString());
    if (!spec) {
      setBuying(false);
      return;
    }
    let cancelled = false;
    setBuying(true);
    setBuyErr("");
    api.get(`/store/${slug}/products/${encodeURIComponent(spec.slug)}`, { params: country ? { country } : {} })
      .then(({ data }) => {
        if (cancelled) return;
        const variants = data.variants || [];
        const variant = (spec.variantId && variants.find((v) => v.id === spec.variantId)) || variants[0] || null;
        replaceWith(data, spec.qty, variant);
        setParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete("buy");
          next.delete("v");
          next.delete("qty");
          return next;
        }, { replace: true });
      })
      .catch(() => {
        if (!cancelled) setBuyErr("This product is not available for checkout.");
      })
      .finally(() => {
        if (!cancelled) setBuying(false);
      });
    return () => { cancelled = true; };
  }, [loaded, slug, country, buyKey, buyVariant, buyQty, replaceWith, setParams]);

  useEffect(() => {
    if (!user || !config.customer_refer_active) { setWalletInfo(null); return; }
    api.get(`/store/${slug}/wallet`).then(({ data }) => setWalletInfo(data)).catch(() => setWalletInfo(null));
  }, [user, slug, config.customer_refer_active]);

  const walletAmt = useWallet && walletInfo?.can_spend ? (walletInfo.balance || 0) : 0;

  useEffect(() => {
    if (!boxOrder && !abpOrder && items.length === 0) return;
    const payload = boxOrder
      ? { items: [], box: { box_id: boxOrder.box_id, product_ids: boxOrder.product_ids }, address, payment_method: payment, coupon_code: couponApplied?.code || null, wallet_amount: walletAmt }
      : abpOrder
      ? { items: [], abp: { bundle_id: abpOrder.bundle_id, target_kg: abpOrder.target_kg, mix_name: abpOrder.mix_name, items: abpOrder.items, service_ids: abpOrder.service_ids || [] }, address, payment_method: payment, coupon_code: couponApplied?.code || null, wallet_amount: walletAmt }
      : { ...cartItemsPayload(items, country), address, payment_method: payment, coupon_code: couponApplied?.code || null, wallet_amount: walletAmt };
    api.post(`/store/${slug}/quote`, payload).then(({ data }) => {
      setQuote(data);
      if (data?.online_discount?.applied) {
        setOnlinePopup(data.online_discount);
      }
    }).catch(() => setQuote(null));
  }, [items, slug, address, payment, couponApplied, walletAmt, country, boxOrder, abpOrder]);

  useEffect(() => {
    if (boxOrder || abpOrder) return;
    if (!loaded || buying || buyErr || items.length === 0) return;
    if (paused) return;
    if (needSlot && !deliverySlotId) return;
    if (!useGokwik || gkLaunched.current) return;
    gkLaunched.current = true;
    let cancelled = false;
    setPlacing(true);
    openGokwikCheckout({
      slug,
      items,
      country,
      couponCode: couponApplied?.code,
      customerEmail: contact.email || user?.email,
      customerName: contact.name || user?.name,
      config,
      deliverySlotId,
    }).then((res) => {
      if (cancelled) return;
      if (res?.status === "complete") {
        const oid = res.order?.merchant_order_id || res.order?.order_id || res.order?.id;
        clear();
        toast.success("Order placed");
        if (oid) nav(`${base}/order/${oid}`);
        else nav(`${base}/`);
        return;
      }
      nav(`${base}/cart`);
    }).catch((e) => {
      if (cancelled) return;
      toast.error(extractErr(e) || "GoKwik could not start. Using original checkout.");
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("native", "1");
        return next;
      }, { replace: true });
      gkLaunched.current = false;
    }).finally(() => {
      if (!cancelled) setPlacing(false);
    });
    return () => { cancelled = true; };
  }, [loaded, buying, buyErr, items.length, useGokwik, paused, needSlot, deliverySlotId, boxOrder]);

  useEffect(() => {
    if (boxOrder || abpOrder) return;
    if (!loaded || buying || buyErr || items.length === 0) return;
    if (paused) return;
    if (needSlot && !deliverySlotId) return;
    if (!useFastrr || frLaunched.current) return;
    frLaunched.current = true;
    let cancelled = false;
    setPlacing(true);
    openFastrrCheckout({
      slug,
      items,
      country,
      couponCode: couponApplied?.code,
      customerEmail: contact.email || user?.email,
      customerName: contact.name || user?.name,
      config,
      deliverySlotId,
    }).then(async (res) => {
      if (cancelled) return;
      if (res?.status === "complete") {
        let oid = res.order?.order_id || res.order?.id || res.order?.merchant_order_id;
        if (!oid && res.sessionId) {
          try {
            const st = await api.get(`/store/${slug}/fastrr/session/${res.sessionId}`);
            oid = st.data?.order_id;
          } catch { /* webhook may land later */ }
        }
        clear();
        toast.success("Order placed");
        if (oid) nav(`${base}/order/${oid}`);
        else nav(`${base}/`);
        return;
      }
      nav(`${base}/cart`);
    }).catch((e) => {
      if (cancelled) return;
      toast.error(extractErr(e) || "Fastrr could not start. Using original checkout.");
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("native", "1");
        return next;
      }, { replace: true });
      frLaunched.current = false;
    }).finally(() => {
      if (!cancelled) setPlacing(false);
    });
    return () => { cancelled = true; };
  }, [loaded, buying, buyErr, items.length, useFastrr, paused, needSlot, deliverySlotId, boxOrder]);

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) return;
    setCouponBusy(true); setCouponErr("");
    try {
      const { data } = await api.post(`/store/${slug}/coupons/validate`, boxOrder
        ? { code, items: [], box: { box_id: boxOrder.box_id, product_ids: boxOrder.product_ids } }
        : abpOrder
        ? { code, items: [], abp: { bundle_id: abpOrder.bundle_id, target_kg: abpOrder.target_kg, mix_name: abpOrder.mix_name, items: abpOrder.items, service_ids: abpOrder.service_ids || [] } }
        : { code, ...cartItemsPayload(items, country) });
      setCouponApplied(data);
      toast.success(`Coupon applied · ${money(data.discount, quote?.symbol || symbolOf(items[0]))} off`);
    } catch (e) {
      const msg = extractErr(e);
      setCouponErr(msg);
      setCouponApplied(null);
    } finally { setCouponBusy(false); }
  };

  const removeCoupon = () => { setCouponApplied(null); setCouponCode(""); setCouponErr(""); };

  if (buying || !loaded) {
    return <div className="max-w-3xl mx-auto px-6 py-16 text-center text-stone-500" data-testid="co-direct-loading">Preparing checkout…</div>;
  }
  if (buyErr) {
    return <div className="max-w-3xl mx-auto px-6 py-16 text-center text-stone-500" data-testid="co-direct-error">{buyErr} <button className="text-stone-900 underline" onClick={() => nav(`${base}/`)}>{t("store.continueShopping")}</button></div>;
  }
  if (!boxOrder && !abpOrder && items.length === 0) {
    return <div className="max-w-3xl mx-auto px-6 py-16 text-center text-stone-500">{t("store.emptyCart")} <button className="text-stone-900 underline" onClick={() => nav(`${base}/`)}>{t("store.continueShopping")}</button></div>;
  }

  if (useGokwik) {
    return (
      <div className="max-w-lg mx-auto px-6 py-16 text-center" data-testid="co-gokwik">
        <h1 className="font-display text-2xl font-bold text-stone-900">GoKwik Checkout</h1>
        <p className="text-stone-500 text-sm mt-2">{placing ? "Opening secure checkout…" : "Complete your order in the GoKwik window."}</p>
        <Button
          type="button"
          className="mt-6 btn-store-primary rounded-full px-6"
          data-testid="co-gokwik-retry"
          onClick={() => { gkLaunched.current = false; setPlacing(true); window.location.reload(); }}
        >
          Open GoKwik
        </Button>
        <button
          type="button"
          className="block mx-auto mt-4 text-xs text-stone-500 underline"
          data-testid="co-gokwik-native"
          onClick={() => {
            gkLaunched.current = true;
            setParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set("native", "1");
              return next;
            }, { replace: true });
          }}
        >
          Use original checkout
        </button>
      </div>
    );
  }

  if (useFastrr) {
    return (
      <div className="max-w-lg mx-auto px-6 py-16 text-center" data-testid="co-fastrr">
        <h1 className="font-display text-2xl font-bold text-stone-900">Fastrr Checkout</h1>
        <p className="text-stone-500 text-sm mt-2">{placing ? "Opening secure checkout…" : "Complete your order in the Shiprocket Fastrr window."}</p>
        <Button
          type="button"
          className="mt-6 btn-store-primary rounded-full px-6"
          data-testid="co-fastrr-retry"
          onClick={() => { frLaunched.current = false; setPlacing(true); window.location.reload(); }}
        >
          Open Fastrr
        </Button>
        <button
          type="button"
          className="block mx-auto mt-4 text-xs text-stone-500 underline"
          data-testid="co-fastrr-native"
          onClick={() => {
            frLaunched.current = true;
            setParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set("native", "1");
              return next;
            }, { replace: true });
          }}
        >
          Use original checkout
        </button>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (quote?.deliverable === false) {
      toast.error(quote.shipping_error || "We do not deliver to this location");
      return;
    }
    if (paused) {
      toast.error(storeOfflineMessage(config));
      return;
    }
    if (needSlot && !deliverySlotId) {
      toast.error(slotPrompt(config));
      return;
    }
    if (!user && !guestAllowed && emailExists) {
      toast.error("Already a customer. Sign in here");
      return;
    }
    setPlacing(true);
    try {
      const body = {
        items: (boxOrder || abpOrder) ? [] : cartItemsPayload(items, country).items,
        box: boxOrder ? { box_id: boxOrder.box_id, product_ids: boxOrder.product_ids } : undefined,
        abp: abpOrder ? { bundle_id: abpOrder.bundle_id, target_kg: abpOrder.target_kg, mix_name: abpOrder.mix_name, items: abpOrder.items, service_ids: abpOrder.service_ids || [] } : undefined,
        address,
        payment_method: payment,
        customer_email: contact.email,
        customer_name: contact.name,
        password: !user && !guestAllowed ? password : undefined,
        notes,
        coupon_code: couponApplied?.code || null,
        wallet_amount: walletAmt,
        country,
        delivery_slot_id: deliverySlotId || null,
      };
      const { data } = await api.post(`/store/${slug}/checkout`, body);
      if (data.razorpay_order_id) {
        const ok = await loadRazorpay();
        if (!ok) {
          toast.error("Could not load Razorpay. Please try again.");
          setPlacing(false);
          return;
        }
        const rzp = new window.Razorpay({
          key: data.razorpay_key_id,
          amount: data.amount_paise,
          currency: data.currency || quote?.currency || "INR",
          order_id: data.razorpay_order_id,
          name: config.store_info?.name || "Store",
          description: `Order ${data.order_number}`,
          image: config.store_info?.logo_url || undefined,
          prefill: {
            name: contact.name || address.name || (user?.name || ""),
            email: contact.email || (user?.email || ""),
            contact: address.phone || "",
          },
          theme: { color: config.branding?.primary_color || "#0f172a" },
          modal: {
            ondismiss: () => {
              setPlacing(false);
              toast.error("Payment cancelled. Your order was saved but not paid.");
              nav(`${base}/order/${data.order_id}`);
            },
          },
          handler: async (resp) => {
            try {
              await api.post(`/store/${slug}/razorpay/verify`, {
                order_id: data.order_id,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              if (!boxOrder && !abpOrder) clear();
              if (data.account_logged_in) { try { await refresh(); } catch { /* ignore */ } }
              toast.success("Payment successful!");
              nav(`${base}/order/${data.order_id}`);
            } catch (err) {
              toast.error(extractErr(err));
              nav(`${base}/order/${data.order_id}`);
            } finally {
              setPlacing(false);
            }
          },
        });
        rzp.on("payment.failed", (resp) => {
          toast.error(resp?.error?.description || "Payment failed");
          setPlacing(false);
          nav(`${base}/order/${data.order_id}`);
        });
        rzp.open();
        return;
      }

      // Cashfree — hosted checkout via SDK, returns to order page (?gw=cashfree)
      if (data.cashfree_payment_session_id) {
        if (!boxOrder && !abpOrder) clear();
        const cashfree = await load({ mode: data.cashfree_mode || "sandbox" });
        await cashfree.checkout({ paymentSessionId: data.cashfree_payment_session_id, redirectTarget: "_self" });
        return;
      }

      if (data.redirect_url) {
        if (!boxOrder && !abpOrder) clear();
        window.location.assign(data.redirect_url);
        return;
      }

      // WhatsApp — record the order, then open api.whatsapp.com/send
      if (payment === "whatsapp" && data.whatsapp_url) {
        if (!boxOrder && !abpOrder) clear();
        if (data.account_logged_in) { try { await refresh(); } catch { /* ignore */ } }
        toast.success("WhatsApp order saved — confirm it in the chat.");
        window.open(data.whatsapp_url, "_blank", "noopener,noreferrer");
        nav(`${base}/order/${data.order_id}`);
        return;
      }
      if (data.payu_action && data.payu_fields) {
        if (!boxOrder && !abpOrder) clear();
        const form = document.createElement("form");
        form.method = "POST";
        form.action = data.payu_action;
        Object.entries(data.payu_fields).forEach(([k, v]) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = k;
          input.value = v == null ? "" : String(v);
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
        return;
      }

      if (!boxOrder && !abpOrder) clear();
      if (data.account_logged_in) { try { await refresh(); } catch { /* ignore */ } }
      toast.success(data.account_logged_in ? "Order placed! You're now signed in." : "Order placed!");
      nav(`${base}/order/${data.order_id}`);
    } catch (e) { toast.error(extractErr(e)); setPlacing(false); }
  };

  const total = quote?.total ?? (boxOrder ? Number(boxOrder.price) || 0 : abpOrder ? Number(abpOrder.price) || 0 : subtotal);
  const perShip = quote?.shipping_matched === "per_product";
  const summaryLines = boxOrder
    ? (quote?.items?.length ? quote.items : [{ name: boxOrder.name, qty: 1, line_total: Number(boxOrder.price) || 0, product_id: boxOrder.box_id }])
    : abpOrder
    ? (quote?.items?.length ? quote.items : [{ name: abpOrder.name, qty: 1, line_total: Number(abpOrder.price) || 0, product_id: abpOrder.bundle_id }])
    : (perShip && quote?.items?.length ? quote.items : items);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="store-checkout">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-stone-900">{t("store.checkout")}</h1>
      {paused ? (
        <div className="mt-4 rounded-xl bg-stone-900 text-white px-4 py-3 text-sm" data-testid="co-store-offline">{storeOfflineMessage(config)}</div>
      ) : null}
      <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-4">
          {!user && (
            <Card className="p-5 border-stone-200/70">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Contact</div>
                <button type="button" className="text-xs font-semibold underline underline-offset-2 text-stone-600 hover:text-stone-900" data-testid="co-signin-link" onClick={() => nav(`${base}/account/login?redirect=checkout`)}>Sign in</button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Full name</Label><Input required data-testid="co-name" className="mt-1" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} /></div>
                <div><Label>Email</Label><Input required type="email" data-testid="co-email" className="mt-1" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} /></div>
              </div>
              {!guestAllowed && (
                <div className="mt-3">
                  <Label>Password</Label>
                  <Input required={!emailExists} type="password" minLength={6} autoComplete="new-password" data-testid="co-password" className="mt-1" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" />
                  <p className="mt-2 text-xs text-stone-500" data-testid="co-account-note">Choose a password to create your account. You&apos;ll be signed in after checkout and can use it to sign in again.</p>
                </div>
              )}
              {emailExists ? (
                <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="co-existing-customer">
                  Already a customer. <Link to={`${base}/account/login?redirect=checkout`} className="font-semibold underline" data-testid="co-existing-signin">Sign in here</Link>
                </p>
              ) : null}
            </Card>
          )}

          <Card className="p-5 border-stone-200/70">
            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-3">Shipping address</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Full name</Label><Input required data-testid="co-addr-name" className="mt-1" value={address.name} onChange={(e) => setAddress({ ...address, name: e.target.value })} /></div>
              <div><Label>Phone</Label><Input required data-testid="co-addr-phone" className="mt-1" value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} /></div>
              <div>
                <Label>Pincode</Label>
                <Input
                  required
                  data-testid="co-addr-pin"
                  className="mt-1"
                  inputMode="numeric"
                  maxLength={6}
                  pattern="\d{6}"
                  title="6-digit Indian pincode"
                  value={address.pincode}
                  onChange={(e) => setAddress({ ...address, pincode: e.target.value.replace(/\D/g, "").slice(0, 6), country: "India" })}
                />
              </div>
              <div className="col-span-2"><Label>Address line 1</Label><Input required data-testid="co-addr-line1" className="mt-1" value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} /></div>
              <div className="col-span-2"><Label>Address line 2 <span className="text-stone-400 text-xs">optional</span></Label><Input data-testid="co-addr-line2" className="mt-1" value={address.line2} onChange={(e) => setAddress({ ...address, line2: e.target.value })} /></div>
              <div><Label>City</Label><Input required data-testid="co-addr-city" className="mt-1" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} /></div>
              <div>
                <Label>State</Label>
                <select
                  required
                  data-testid="co-addr-state"
                  className="mt-1 w-full h-10 rounded-md border border-stone-200 bg-white px-3 text-sm"
                  value={address.state}
                  onChange={(e) => setAddress({ ...address, state: e.target.value, country: "India" })}
                >
                  <option value="">Select state / UT</option>
                  {IN_STATES.map((st) => (
                    <option key={st} value={st}>{st}</option>
                  ))}
                </select>
              </div>
            </div>
          </Card>

          <Card className="p-5 border-stone-200/70">
            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-3">Payment</div>
            <RadioGroup value={payment} onValueChange={setPayment} className="space-y-2">
              {config.payment?.cod_enabled && (
                <label className="flex items-center gap-3 p-3 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-50" data-testid="co-pay-cod">
                  <RadioGroupItem value="cod" id="cod" />
                  <div>
                    <div className="font-semibold text-stone-900">Cash on Delivery</div>
                    <div className="text-xs text-stone-500">Pay in cash when your order arrives.</div>
                    {quote?.cod_partial?.eligible && (
                      <div className="mt-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-md px-2 py-1.5" data-testid="co-cod-split">
                        Pay now {money(quote.cod_partial.pay_now, quote.symbol)} · Pay on delivery {money(quote.cod_partial.pay_on_delivery, quote.symbol)}
                      </div>
                    )}
                  </div>
                </label>
              )}
              {config.payment?.razorpay_enabled && (
                <label className="flex items-center gap-3 p-3 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-50" data-testid="co-pay-razorpay">
                  <RadioGroupItem value="razorpay" id="razorpay" />
                  <div><div className="font-semibold text-stone-900">Razorpay</div><div className="text-xs text-stone-500">Pay securely with UPI, cards, wallets or netbanking.</div></div>
                </label>
              )}
              {config.payment?.cashfree_enabled && (
                <label className="flex items-center gap-3 p-3 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-50" data-testid="co-pay-cashfree">
                  <RadioGroupItem value="cashfree" id="cashfree" />
                  <div><div className="font-semibold text-stone-900">Cashfree</div><div className="text-xs text-stone-500">Pay securely with UPI, cards, wallets or netbanking.</div></div>
                </label>
              )}
              {config.payment?.instamojo_enabled && (
                <label className="flex items-center gap-3 p-3 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-50" data-testid="co-pay-instamojo">
                  <RadioGroupItem value="instamojo" id="instamojo" />
                  <div><div className="font-semibold text-stone-900">Instamojo</div><div className="text-xs text-stone-500">Pay securely via Instamojo hosted checkout.</div></div>
                </label>
              )}
              {config.payment?.payu_enabled && (
                <label className="flex items-center gap-3 p-3 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-50" data-testid="co-pay-payu">
                  <RadioGroupItem value="payu" id="payu" />
                  <div><div className="font-semibold text-stone-900">PayU</div><div className="text-xs text-stone-500">Pay securely with UPI, cards, wallets or netbanking.</div></div>
                </label>
              )}
              {config.payment?.phonepe_enabled && (
                <label className="flex items-center gap-3 p-3 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-50" data-testid="co-pay-phonepe">
                  <RadioGroupItem value="phonepe" id="phonepe" />
                  <div><div className="font-semibold text-stone-900">PhonePe</div><div className="text-xs text-stone-500">Pay securely with UPI, cards or netbanking via PhonePe.</div></div>
                </label>
              )}
              {config.payment?.stripe_enabled && (
                <label className="flex items-center gap-3 p-3 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-50" data-testid="co-pay-stripe">
                  <RadioGroupItem value="stripe" id="stripe" />
                  <div><div className="font-semibold text-stone-900">Stripe</div><div className="text-xs text-stone-500">Pay with card on Stripe’s secure checkout page.</div></div>
                </label>
              )}
              {config.payment?.paypal_enabled && (
                <label className="flex items-center gap-3 p-3 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-50" data-testid="co-pay-paypal">
                  <RadioGroupItem value="paypal" id="paypal" />
                  <div><div className="font-semibold text-stone-900">PayPal</div><div className="text-xs text-stone-500">Pay with your PayPal balance, card, or bank.</div></div>
                </label>
              )}
              {config.payment?.whatsapp_enabled && (
                <label className="flex items-center gap-3 p-3 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-50" data-testid="co-pay-whatsapp">
                  <RadioGroupItem value="whatsapp" id="whatsapp" />
                  <div><div className="font-semibold text-stone-900">WhatsApp order</div><div className="text-xs text-stone-500">Send this order on WhatsApp. The store confirms it manually.</div></div>
                </label>
              )}
            </RadioGroup>
          </Card>

          {needSlot ? (
            <Card className="p-5 border-stone-200/70" data-testid="co-timeslot">
              <div className="font-semibold text-stone-900">{slotPrompt(config)}</div>
              <RadioGroup className="mt-3 space-y-2" value={deliverySlotId} onValueChange={setDeliverySlotId}>
                {slotList.map((slot) => (
                  <label key={slot.id} className="flex items-center gap-3 p-3 rounded-lg border border-stone-200 cursor-pointer hover:bg-stone-50" data-testid={`co-slot-${slot.id}`}>
                    <RadioGroupItem value={slot.id} id={`slot-${slot.id}`} />
                    <div className="font-medium text-stone-900">{slot.label}</div>
                  </label>
                ))}
              </RadioGroup>
            </Card>
          ) : null}
          <Card className="p-5 border-stone-200/70">
            <Label>Order notes <span className="text-stone-400 text-xs">optional</span></Label>
            <Textarea rows={3} data-testid="co-notes" className="mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Card>
        </div>

        <Card className="p-5 border-stone-200/70 h-fit sticky top-24">
          <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Order</div>
          <div className="mt-3 space-y-1 text-sm">
            {summaryLines.map((it) => (
              <div key={it.line_id || it.product_id || it.box_id} className="space-y-1">
                <div className="flex justify-between gap-2">
                  <span className="text-stone-600 truncate min-w-0">{it.name} × {it.qty}</span>
                  <span className="font-mono-nums flex-shrink-0">{money(it.line_total != null ? it.line_total : it.price * it.qty, quote?.symbol || symbolOf(it))}</span>
                </div>
                {perShip && (
                  <div className="text-[11px] text-stone-400" data-testid="co-line-shipping">Shipping {money(it.shipping_line || 0, quote?.symbol || symbolOf(it))}</div>
                )}
                {it.is_box && (it.box_items || []).length ? (
                  <ul className="pl-3 text-xs text-stone-500" data-testid="co-box-items">
                    {it.box_items.map((b) => <li key={b.product_id}>{b.name} × {b.qty}</li>)}
                  </ul>
                ) : null}
                {it.is_abp && it.abp ? (
                  <ul className="pl-3 text-xs text-stone-500" data-testid="co-abp-items">
                    <li>{it.abp.target_kg} KG{it.abp.mix_name ? ` · ${it.abp.mix_name}` : ""}</li>
                    {(it.abp.items || []).map((b) => <li key={b.product_id}>{b.name} × {b.qty_kg} KG</li>)}
                    {(it.abp.services || []).map((s, i) => <li key={i}>{s.name}: ₹{s.amount}</li>)}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>

          {/* Coupon */}
          <div className="mt-4 border-t border-stone-100 pt-3">
            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Have a coupon?</div>
            {couponApplied ? (
              <div className="flex items-center justify-between p-2.5 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="co-coupon-applied">
                <div>
                  <div className="font-mono-nums font-bold text-emerald-800 tracking-wider">{couponApplied.code}</div>
                  <div className="text-xs text-emerald-700">−{money(couponApplied.discount, quote?.symbol || symbolOf(items[0]))} applied</div>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={removeCoupon} data-testid="co-coupon-remove" className="text-emerald-800 hover:text-emerald-900 h-8">Remove</Button>
              </div>
            ) : (
              <div>
                <div className="flex gap-2">
                  <Input placeholder="WELCOME10" data-testid="co-coupon-input" className="font-mono-nums uppercase" value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} />
                  <Button type="button" variant="outline" onClick={applyCoupon} disabled={couponBusy || !couponCode.trim()} data-testid="co-coupon-apply">{couponBusy ? "…" : "Apply"}</Button>
                </div>
                {couponErr && <div className="text-xs text-red-600 mt-1" data-testid="co-coupon-error">{couponErr}</div>}
              </div>
            )}
          </div>

          <div className="mt-4 border-t border-stone-100 pt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-stone-500">Subtotal</span><span className="font-mono-nums">{money(quote?.subtotal ?? (boxOrder ? Number(boxOrder.price) || 0 : subtotal), quote?.symbol || symbolOf(items[0] || summaryLines[0]))}</span></div>
            <div className="flex justify-between"><span className="text-stone-500">Shipping</span><span className="font-mono-nums" data-testid="co-shipping-amount">{quote?.deliverable === false ? "—" : ((quote?.shipping ?? 0) === 0 ? "Free" : money(quote.shipping, quote?.symbol || symbolOf(items[0])))}</span></div>
            {quote?.deliverable === false && quote?.shipping_error && (
              <div className="text-xs text-red-600" data-testid="co-shipping-error">{quote.shipping_error}</div>
            )}
            {quote?.discount > 0 && (
              <div className="flex justify-between text-emerald-700"><span>Discount ({quote?.coupon?.code})</span><span className="font-mono-nums">−{money(quote.discount, quote?.symbol || symbolOf(items[0]))}</span></div>
            )}
            {quote?.online_discount?.applied && (
              <div className="flex justify-between text-emerald-700" data-testid="co-online-discount">
                <span>Online payment ({quote.online_discount.code} · {quote.online_discount.percent}%)</span>
                <span className="font-mono-nums">−{money(quote.online_discount.amount, quote?.symbol || symbolOf(items[0]))}</span>
              </div>
            )}
            {(quote?.charges || []).map((c) => (
              <div key={c.id || c.name} className="flex justify-between" data-testid="co-charge">
                <span className="text-stone-500">{c.name}{c.kind === "percent" ? ` (${c.value}%)` : ""}</span>
                <span className="font-mono-nums">{money(c.amount, quote?.symbol || symbolOf(items[0]))}</span>
              </div>
            ))}
            {walletInfo?.can_spend && Number(walletInfo.balance || 0) > 0 && (
              <label className="flex items-center justify-between gap-2 text-sm py-1" data-testid="co-wallet-toggle">
                <span>Use wallet ({money(walletInfo.balance, "₹")})</span>
                <input type="checkbox" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)} />
              </label>
            )}
            {quote?.wallet > 0 && (
              <div className="flex justify-between text-emerald-700"><span>Wallet</span><span className="font-mono-nums">−{money(quote.wallet, quote?.symbol || "₹")}</span></div>
            )}
            {quote?.cod_partial?.eligible && (
              <div className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50 p-3 space-y-1 text-sm" data-testid="co-pay-now-breakdown">
                <div className="flex justify-between text-emerald-900 font-medium"><span>Pay now</span><span className="font-mono-nums">{money(quote.cod_partial.pay_now, quote.symbol)}</span></div>
                <div className="flex justify-between text-emerald-800"><span>Pay on delivery</span><span className="font-mono-nums">{money(quote.cod_partial.pay_on_delivery, quote.symbol)}</span></div>
              </div>
            )}
            <div className="flex justify-between font-display font-bold text-lg pt-2 mt-2 border-t border-stone-100"><span>Total</span><span className="font-mono-nums">{money(total, quote?.symbol || symbolOf(items[0] || summaryLines[0]))}</span></div>
          </div>
          <Button type="submit" disabled={placing || paused || emailExists || quote?.deliverable === false || (needSlot && !deliverySlotId)} data-testid="co-place-order" className="w-full mt-5 btn-store-primary rounded-full h-11">{paused ? storeOfflineMessage(config) : (placing ? t("store.placing") : t("store.placeOrder"))}</Button>
        </Card>
      </form>
      {onlinePopup && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-stone-900/40" data-testid="co-online-popup" onClick={() => setOnlinePopup(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 px-6 py-8 text-white text-center">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold opacity-90">Online payment</div>
              <div className="mt-3 text-4xl font-black">Save {onlinePopup.percent}%</div>
              <p className="mt-3 text-sm text-white/90">Online payment can save you up to {onlinePopup.percent}% Coupon code applied</p>
              <div className="mt-4 inline-block px-3 py-1 rounded-full bg-white/15 border border-white/30 font-mono tracking-widest text-sm">{onlinePopup.code}</div>
            </div>
            <div className="p-5 flex justify-center">
              <Button type="button" data-testid="co-online-popup-ok" className="rounded-full bg-emerald-600 hover:bg-emerald-700 px-8" onClick={() => setOnlinePopup(null)}>Nice — continue</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
